import React, {ReactNode, useState} from 'react';
import {ID} from "@datorama/akita";
import './PlayerTable.css';
import {FieldsetInput} from "../component-library/Fieldsets";
import {useFuseFilter} from "../hooks/useFuseFilter";
import {Player} from "../domain/player/models";

export type SortColumn = 'firstName' | 'lastName' | 'rating' | 'lastPlayedAt';
export type SortDir = 'asc' | 'desc';

export const ACTIVITY_OPTIONS: {label: string; days: number | null}[] = [
    {label: 'All players', days: null},
    {label: 'Last week', days: 7},
    {label: 'Last 2 weeks', days: 14},
    {label: 'Last 3 months', days: 90},
    {label: 'Last 6 months', days: 180},
    {label: 'Last year', days: 365},
];

export const RATING_OPTIONS: {label: string; min: number | null; max: number | null}[] = [
    {label: 'All Ratings', min: null, max: null},
    {label: 'Under 1000', min: null, max: 1000},
    {label: '1000–1400', min: 1000, max: 1400},
    {label: '1400–1800', min: 1400, max: 1800},
    {label: '1800+', min: 1800, max: null},
];

interface StorageKeys {
    activity: string;
    sortCol: string;
    sortDir: string;
    rating: string;
    neverPlayed: string;
}

function storageKeys(namespace: string): StorageKeys {
    return {
        activity: `${namespace}-activity-filter`,
        sortCol: `${namespace}-sort-col`,
        sortDir: `${namespace}-sort-dir`,
        rating: `${namespace}-rating-filter`,
        neverPlayed: `${namespace}-never-played`,
    };
}

function readActivity(key: string, defaultDays: number | null): number | null {
    const v = localStorage.getItem(key);
    if (v === null) return defaultDays;
    if (v === '') return null;
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? defaultDays : n;
}

function readString<T extends string>(key: string, fallback: T): T {
    return (localStorage.getItem(key) as T) ?? fallback;
}

function readBool(key: string): boolean {
    return localStorage.getItem(key) === 'true';
}

export function applyActivityFilter(players: Player[], activityDays: number | null, neverPlayedOnly: boolean): Player[] {
    if (neverPlayedOnly) {
        return players.filter(p => !p.lastPlayedAt);
    }
    if (activityDays === null) {
        return players;
    }
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - activityDays);
    return players.filter(p => p.lastPlayedAt && new Date(p.lastPlayedAt) >= cutoff);
}

export function applyRatingFilter(players: Player[], ratingLabel: string): Player[] {
    const option = RATING_OPTIONS.find(o => o.label === ratingLabel);
    if (!option || (option.min === null && option.max === null)) return players;
    return players.filter(p => {
        const r = p.rating ?? 0;
        if (option.min !== null && r < option.min) return false;
        if (option.max !== null && r >= option.max) return false;
        return true;
    });
}

export function sortPlayers(players: Player[], col: SortColumn, dir: SortDir): Player[] {
    return [...players].sort((a, b) => {
        let cmp = 0;
        switch (col) {
            case 'firstName':
                cmp = (a.firstName ?? '').localeCompare(b.firstName ?? '');
                break;
            case 'lastName':
                cmp = (a.lastName ?? '').localeCompare(b.lastName ?? '');
                break;
            case 'rating':
                cmp = (a.rating ?? 0) - (b.rating ?? 0);
                break;
            case 'lastPlayedAt': {
                const aTime = a.lastPlayedAt ? new Date(a.lastPlayedAt).getTime() : 0;
                const bTime = b.lastPlayedAt ? new Date(b.lastPlayedAt).getTime() : 0;
                cmp = aTime - bTime;
                break;
            }
        }
        return dir === 'asc' ? cmp : -cmp;
    });
}

export function formatLastPlayed(lastPlayedAt?: string): string {
    if (!lastPlayedAt) return '—';
    return new Date(lastPlayedAt.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
}

export interface PlayerFilterableTableProps {
    players: Player[];
    storageNamespace: string;
    defaultActivityDays?: number | null;
    onRowClick?: (player: Player) => void;
    activePlayerId?: ID;
    renderRowActions?: (player: Player) => ReactNode;
    renderHeaderActions?: (params: {
        activityDays: number | null;
        ratingFilter: string;
        neverPlayedOnly: boolean;
        sortColumn: SortColumn;
        sortDir: SortDir;
    }) => ReactNode;
    extraFilter?: (players: Player[]) => Player[];
    renderExtraFilters?: ReactNode;
}

export function PlayerFilterableTable({
    players,
    storageNamespace,
    defaultActivityDays = null,
    onRowClick,
    activePlayerId,
    renderRowActions,
    renderHeaderActions,
    extraFilter,
    renderExtraFilters,
}: PlayerFilterableTableProps) {
    const keys = storageKeys(storageNamespace);

    const [filteredPlayers, playerCriteria, setPlayerCriteria] =
        useFuseFilter<Player>(players, {keys: ["firstName", "lastName", "rating"]});

    const [activityFilter, setActivityFilterRaw] =
        useState<number | null>(() => readActivity(keys.activity, defaultActivityDays ?? null));
    const [ratingFilter, setRatingFilterRaw] =
        useState<string>(() => readString(keys.rating, 'All Ratings'));
    const [neverPlayedOnly, setNeverPlayedOnlyRaw] =
        useState<boolean>(() => readBool(keys.neverPlayed));
    const [sortColumn, setSortColumnRaw] =
        useState<SortColumn>(() => readString<SortColumn>(keys.sortCol, 'rating'));
    const [sortDir, setSortDirRaw] =
        useState<SortDir>(() => readString<SortDir>(keys.sortDir, 'desc'));

    function setActivityFilter(days: number | null) {
        setActivityFilterRaw(days);
        localStorage.setItem(keys.activity, days === null ? '' : String(days));
    }

    function setRatingFilter(label: string) {
        setRatingFilterRaw(label);
        localStorage.setItem(keys.rating, label);
    }

    function setNeverPlayedOnly(val: boolean) {
        setNeverPlayedOnlyRaw(val);
        localStorage.setItem(keys.neverPlayed, String(val));
    }

    function setSortColumn(col: SortColumn) {
        setSortColumnRaw(col);
        localStorage.setItem(keys.sortCol, col);
    }

    function setSortDir(dir: SortDir) {
        setSortDirRaw(dir);
        localStorage.setItem(keys.sortDir, dir);
    }

    function handleColumnClick(col: SortColumn) {
        if (col === sortColumn) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(col);
            setSortDir('asc');
        }
    }

    const preFiltered = extraFilter ? extraFilter(filteredPlayers) : filteredPlayers;
    const displayedPlayers = sortPlayers(
        applyRatingFilter(
            applyActivityFilter(preFiltered, activityFilter, neverPlayedOnly),
            ratingFilter
        ),
        sortColumn,
        sortDir
    );

    function sortIndicator(col: SortColumn) {
        if (col !== sortColumn) return null;
        return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
    }

    return (
        <div className="column-contents">
            <header className="player-table-header">
                <div className="player-table-header-title player-table-header-column">
                    <FieldsetInput
                        label={"Filter"}
                        id={`filter-players-${storageNamespace}`}
                        value={playerCriteria}
                        onChange={(v) => setPlayerCriteria(v)}/>
                </div>
                {renderHeaderActions && (
                    <div className="player-table-header-actions player-table-header-column">
                        {renderHeaderActions({
                            activityDays: activityFilter,
                            ratingFilter,
                            neverPlayedOnly,
                            sortColumn,
                            sortDir,
                        })}
                    </div>
                )}
            </header>
            <div className="player-table-filters">
                <label className="player-table-filter-label">
                    Activity
                    <select
                        className="player-table-filter-select"
                        value={activityFilter ?? ''}
                        disabled={neverPlayedOnly}
                        onChange={e => setActivityFilter(e.target.value === '' ? null : parseInt(e.target.value, 10))}>
                        {ACTIVITY_OPTIONS.map(o => (
                            <option key={o.label} value={o.days ?? ''}>{o.label}</option>
                        ))}
                    </select>
                </label>
                <label className="player-table-filter-label">
                    Rating
                    <select
                        className="player-table-filter-select"
                        value={ratingFilter}
                        onChange={e => setRatingFilter(e.target.value)}>
                        {RATING_OPTIONS.map(o => (
                            <option key={o.label} value={o.label}>{o.label}</option>
                        ))}
                    </select>
                </label>
                <label className="player-table-filter-label player-table-filter-checkbox">
                    <input
                        type="checkbox"
                        checked={neverPlayedOnly}
                        onChange={e => setNeverPlayedOnly(e.target.checked)}/>
                    Never played
                </label>
                {renderExtraFilters}
            </div>
            <table className="player-table">
                <thead>
                <tr>
                    <th className="align-left sortable" tabIndex={0} onClick={() => handleColumnClick('firstName')} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleColumnClick('firstName')}>
                        First Name{sortIndicator('firstName')}
                    </th>
                    <th className="align-left sortable" tabIndex={0} onClick={() => handleColumnClick('lastName')} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleColumnClick('lastName')}>
                        Last Name{sortIndicator('lastName')}
                    </th>
                    <th className="align-right sortable" tabIndex={0} onClick={() => handleColumnClick('rating')} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleColumnClick('rating')}>
                        Rating{sortIndicator('rating')}
                    </th>
                    <th className="align-right sortable" tabIndex={0} onClick={() => handleColumnClick('lastPlayedAt')} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && handleColumnClick('lastPlayedAt')}>
                        Last Played{sortIndicator('lastPlayedAt')}
                    </th>
                    {renderRowActions && <th/>}
                </tr>
                </thead>
                <tbody>
                {displayedPlayers.map(player => {
                    const clickHandler = onRowClick ? () => onRowClick(player) : undefined;
                    const isActive = activePlayerId !== undefined && activePlayerId === player.id;
                    return (
                        <tr key={player.id} className={isActive ? 'selected' : ''}>
                            <td onClick={clickHandler}>{player.firstName}</td>
                            <td onClick={clickHandler}>{player.lastName}</td>
                            <td onClick={clickHandler} className="align-right">{player.rating}</td>
                            <td onClick={clickHandler} className="align-right">{formatLastPlayed(player.lastPlayedAt)}</td>
                            {renderRowActions && (
                                <td className="align-right">{renderRowActions(player)}</td>
                            )}
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
}
